// Country reference list for sourcing markets CC actually deals with, plus
// a long tail of less-common ones. The DB convention (per the
// `crm.suppliers.country` comment in 0100_crm.sql) is ISO-3166-1 alpha-2 —
// "CN", "BD", "IN" — so the form lets the admin TYPE the country name
// (autocomplete via <datalist>) but submits the alpha-2 code.
//
// `name → code` is the lookup direction the form needs, so we keep the
// canonical map keyed by name. `codeToName` is computed on first access for
// edit-mode display.
//
// If a supplier sits in a country not on this list, the form still accepts
// free-text — `normalizeCountry` returns the typed value uppercased so
// nothing is lost. The list is curated, not authoritative.

export interface CountryOption {
  code: string;   // ISO 3166-1 alpha-2
  name: string;   // English short name
  flag: string;   // emoji flag derived from regional indicators
}

// Most common sourcing markets first, then alphabetical for the rest. The
// order matters because <datalist> in some browsers preserves source order
// when the user hasn't typed anything.
export const COUNTRY_OPTIONS: CountryOption[] = [
  // Asia — primary sourcing region for CC
  { code: "CN", name: "China",        flag: "🇨🇳" },
  { code: "HK", name: "Hong Kong",    flag: "🇭🇰" },
  { code: "TW", name: "Taiwan",       flag: "🇹🇼" },
  { code: "BD", name: "Bangladesh",   flag: "🇧🇩" },
  { code: "IN", name: "India",        flag: "🇮🇳" },
  { code: "PK", name: "Pakistan",     flag: "🇵🇰" },
  { code: "LK", name: "Sri Lanka",    flag: "🇱🇰" },
  { code: "VN", name: "Vietnam",      flag: "🇻🇳" },
  { code: "MM", name: "Myanmar",      flag: "🇲🇲" },
  { code: "KH", name: "Cambodia",     flag: "🇰🇭" },
  { code: "ID", name: "Indonesia",    flag: "🇮🇩" },
  { code: "TH", name: "Thailand",     flag: "🇹🇭" },
  { code: "PH", name: "Philippines",  flag: "🇵🇭" },
  { code: "MY", name: "Malaysia",     flag: "🇲🇾" },
  { code: "KR", name: "South Korea",  flag: "🇰🇷" },
  { code: "JP", name: "Japan",        flag: "🇯🇵" },
  { code: "NP", name: "Nepal",        flag: "🇳🇵" },
  { code: "LA", name: "Laos",         flag: "🇱🇦" },
  // Africa & Middle East
  { code: "ZA", name: "South Africa", flag: "🇿🇦" },
  { code: "MA", name: "Morocco",      flag: "🇲🇦" },
  { code: "EG", name: "Egypt",        flag: "🇪🇬" },
  { code: "TN", name: "Tunisia",      flag: "🇹🇳" },
  { code: "ET", name: "Ethiopia",     flag: "🇪🇹" },
  { code: "MU", name: "Mauritius",    flag: "🇲🇺" },
  { code: "MG", name: "Madagascar",   flag: "🇲🇬" },
  { code: "TR", name: "Turkey",       flag: "🇹🇷" },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪" },
  // Europe
  { code: "IT", name: "Italy",        flag: "🇮🇹" },
  { code: "PT", name: "Portugal",     flag: "🇵🇹" },
  { code: "ES", name: "Spain",        flag: "🇪🇸" },
  { code: "FR", name: "France",       flag: "🇫🇷" },
  { code: "DE", name: "Germany",      flag: "🇩🇪" },
  { code: "NL", name: "Netherlands",  flag: "🇳🇱" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "BG", name: "Bulgaria",     flag: "🇧🇬" },
  { code: "RO", name: "Romania",      flag: "🇷🇴" },
  { code: "PL", name: "Poland",       flag: "🇵🇱" },
  // Americas
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "MX", name: "Mexico",       flag: "🇲🇽" },
  { code: "BR", name: "Brazil",       flag: "🇧🇷" },
  { code: "PE", name: "Peru",         flag: "🇵🇪" },
  { code: "CO", name: "Colombia",     flag: "🇨🇴" },
];

const NAME_TO_CODE = new Map<string, string>();
const CODE_TO_NAME = new Map<string, string>();
for (const c of COUNTRY_OPTIONS) {
  NAME_TO_CODE.set(c.name.toLowerCase(), c.code);
  CODE_TO_NAME.set(c.code, c.name);
}

/**
 * Normalize a free-text country input to an ISO-3166-1 alpha-2 code.
 *
 * - "China" → "CN"
 * - "china" → "CN"
 * - "CN"    → "CN"
 * - "cn"    → "CN"
 * - "Klingon Empire" → "KLINGON EMPIRE" (passes through; DB column is
 *   plain text so unusual entries don't get blocked)
 * - "" / null → null
 */
export function normalizeCountry(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Already an ISO-2 code (case-insensitive match against our list)
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && CODE_TO_NAME.has(upper)) return upper;
  // Country name match (case-insensitive)
  const byName = NAME_TO_CODE.get(trimmed.toLowerCase());
  if (byName) return byName;
  // Unknown — pass through uppercased so the row still saves but the
  // admin can recognize the typed value when it shows up in the list.
  return upper;
}

/** Display the full country name for a stored ISO-2 code. Falls back to
 *  the raw value if we don't recognize it. */
export function countryDisplayName(code: string | null | undefined): string {
  if (!code) return "";
  return CODE_TO_NAME.get(code.toUpperCase()) ?? code;
}
